<template>
  <div
    class="ui-tree__item group"
    :class="mainClasses"
    :style="mainStyles"
    @contextmenu.prevent="handleOnSelfContextmenu"
    @click="handleClickSelfNode"
  >
    <slot
      name="before"
      :key-value
      :is-leaf-node
    />
    <TreeItemBranch
      :root-icon="$slots['root-icon']"
      :branches
      :current-branch
      :has-children
      :is-root
      :is-opened
      @click:branch="handleClickBranch"
    />
    <div
      class="ui-tree__item-area"
      :class="areaClasses"
    >
      <div
        v-tooltip.top="nameTooltip"
        class="ui-tree__name"
        :class="nameClasses"
        :data-leaf="isLeafNode || undefined"
        @click="handleClickSelfName"
      >
        <slot
          name="name"
          :is-root
          :formatted-name
          :path
          :wide="isWide"
          :open="handleOpen"
        >
          <span ref="nameRef">
            <component :is="displayName" />
          </span>
        </slot>
        <div
          v-if="description"
          class="ui-tree__description"
        >
          <component :is="displayDescription" />
        </div>
      </div>
      <div
        v-if="meta"
        class="ui-tree__value"
      >
        <slot
          v-bind="meta"
          :search
          :is-root
          :path
        />
      </div>
    </div>
    <div
      v-if="$slots.actions"
      class="ui-tree__actions"
    >
      <slot
        name="actions"
        :key-value="props.keyValue"
        :item="node"
        :is-leaf="isLeafNode"
        :is-root
        :path
      />
    </div>
  </div>
  <template v-if="isOpened">
    <TreeItem
      v-for="([key, data], index) in displayNodes"
      :key="[...props.path, key].join('.')"
      :node="data"
      :key-value="key"
      :path="[...props.path, key]"
      :search-string="currentSearchString"
      :scope-position="index"
      :scope-length="nodes.length"
      :branches="nextBranches"
      :hidable-names
      :active-path
      @click:name="handleClickName"
      @click:node="handleClickNode"
      @on:contextmenu="handleOnContextmenu"
      @container:scroll="handleContainerScroll"
      @container:getscroll="handleGetScroll"
    >
      <!-- @vue-ignore -->
      <template #before="slotProps">
        <slot
          name="before"
          v-bind="slotProps"
        />
      </template>
      <!-- @vue-ignore -->
      <template #default="slotProps">
        <slot v-bind="slotProps" />
      </template>
      <!-- @vue-ignore -->
      <template #name="slotProps">
        <slot
          name="name"
          v-bind="slotProps"
        />
      </template>
      <!-- @vue-ignore -->
      <template
        v-if="$slots.actions"
        #actions="slotProps"
      >
        <slot
          name="actions"
          v-bind="slotProps"
        />
      </template>
    </TreeItem>
    <div
      v-if="isLazyIndicatorVisible"
      ref="lazyRef"
      :class="TREE_LAZY_CLASS"
    />
  </template>
</template>

<script setup lang="ts">
import {
  Ref,
  computed,
  ComputedRef,
  h,
  inject,
  ref,
  watch,
  useTemplateRef,
  nextTick,
} from 'vue'
import { omit, orderBy, pick } from 'lodash'
import { useElementVisibility } from '@vueuse/core'

import {
  Tree,
  TreeBranch,
  TreeNodeDescriptionGetter,
  TreeNodeFormatter,
} from '../utils/types'

import {
  HASH_DELIMITER,
  INFO_SEPARATOR,
  TREE_ITEM_FOUND_CLASS,
  TREE_LAZY_COUNT,
  TREE_LAZY_CLASS,
} from '../utils/const'

import TreeItemBranch from './TreeItemBranch.vue'

type Props = {
  node: Tree
  keyValue: string

  path: string[]
  activePath?: string[]

  searchString?: string

  isRoot?: boolean
  hidableNames?: boolean

  branches?: TreeBranch[]

  scopePosition?: number
  scopeLength?: number
}

type Emits = {
  'click:root-icon': []
  'click:name': [data: Tree, isLeaf: boolean]
  'click:node': [path: string[], value: any]
  'on:contextmenu': [data: Tree, isLeaf: boolean]
  'container:scroll': [top: number]
  'container:getscroll': [callback: (top: number) => void]
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

defineOptions({ inheritAttrs: true })

const lazyRef = useTemplateRef('lazyRef')
const isScrolledDown = useElementVisibility(lazyRef)

const metaFields = inject<string[]>('metaFields', [])
const search = inject<Ref<string>>('search')
const wide = inject<boolean>('wide', false)
const formatter = inject<TreeNodeFormatter>('formatter')
const getDescription = inject<TreeNodeDescriptionGetter>('getDescription')
const sortField = inject<string>('sortField')
const sortDir = inject<'asc' | 'desc'>('sortDir')
const searchResult = inject<ComputedRef<string[]>>('searchResult')
const expandedPaths = inject<Ref<Set<string>>>('expandedPaths')

const nameRef = ref<HTMLElement>()

const itemsPerPage = ref(TREE_LAZY_COUNT)
const scrollTopValue = ref<number>()

const hashedPath = computed(() => props.path.join(HASH_DELIMITER))

const isItLastNode = computed(
  () => props.scopeLength === Number(props.scopePosition) + 1,
)

const currentBranch = computed(() => (isItLastNode.value ? 'l' : 'x'))
const nextBranches = computed(() => {
  if (props.isRoot) return []
  const result = [...(props.branches || [])]
  if (isItLastNode.value) {
    result.push('o')
  } else {
    result.push('i')
  }
  return result
})

const meta = computed(() => pick(props.node, metaFields))

const nodes = computed(() => {
  const data = omit(props.node, metaFields)
  const entries = Object.entries<Tree>(data)
  if (sortField) {
    return orderBy(entries, ([, item]) => item[sortField], sortDir)
  } else {
    return entries
  }
})

const displayNodes = computed(() => {
  if (nodes.value.length < itemsPerPage.value) return nodes.value
  return nodes.value.slice(0, itemsPerPage.value)
})

const isOpened = computed(() => {
  return props.isRoot ||
    (expandedPaths?.value?.has(hashedPath.value) && !search?.value) ||
    (!!search?.value && (isFoundBranch.value || isFound.value))
})

const isLazyIndicatorVisible = computed(
  () => isOpened.value && itemsPerPage.value < nodes.value.length,
)

const hasChildren = computed(() => !!nodes.value.length)

const isFound = computed(
  () => !props.isRoot && !!searchResult?.value.includes(currentSearchString.value.toLowerCase()),
)

const isHashedPathDescendant = (ancestor: string, path: string) =>
  path === ancestor || path.startsWith(`${ancestor}${HASH_DELIMITER}`)

const isFoundBranch = computed(() => {
  const prefix = currentSearchString.value.toLowerCase()
  return !!searchResult?.value.some(key => isHashedPathDescendant(prefix, key))
})

const mainStyles = computed(() => ({
  top: `${nextBranches.value.length * (wide ? 3 : 1.5)}rem`,
}))

const mainClasses = computed(() => ({
  'ui-tree__item--wide': wide,
  [TREE_ITEM_FOUND_CLASS]: isFound.value,
  'ui-tree__item--active': isActivePath.value,
  'cursor-pointer': !!props.activePath,
}))

const areaClasses = computed(() => ({
  'ui-tree__item-area--root': props.isRoot,
}))

const isLeafNode = computed(() => {
  const children = omit(props.node, metaFields || [])
  return !Object.keys(children).length
})

const isWide = computed(() => wide && !description.value)

const nameClasses = computed(() => ({
  'ui-tree__name--wide': isWide.value,
  'ui-tree__name--static': !!props.activePath,
}))

const formattedName = computed(() =>
  formatter ? formatter(props.keyValue) : props.keyValue,
)

const displayName = computed(() => h(
  'span',
  { class: props.hidableNames ? 'asset-node' : undefined },
  markSearchedValue(formattedName.value),
))

const description = computed(() => {
  if (props.isRoot || !getDescription) return
  const value = getDescription(props.keyValue)
  if (value === formattedName.value) return
  return value
})

const displayDescription = computed(() => description.value && h(
  'span',
  { class: props.hidableNames ? 'asset-node' : undefined },
  markSearchedValue(description.value),
))

const currentSearchName = computed(
  () =>
    `${formattedName.value}${description.value ? `${INFO_SEPARATOR}${description.value}` : ''}`,
)

const currentSearchString = computed(() => {
  const value = props.isRoot ? props.keyValue : currentSearchName.value
  return `${props.searchString ? `${props.searchString}${HASH_DELIMITER}` : ''}${value}`
})

const isNameEllipsis = computed(() => {
  return nameRef.value && nameRef.value.offsetWidth < nameRef.value.scrollWidth
})

const nameTooltip = computed(() => {
  return isNameEllipsis.value ? formattedName.value : undefined
})

const isActivePath = computed(() => {
  return props.activePath ? JSON.stringify(props.activePath) === JSON.stringify(props.path) : false
})

const markSearchedValue = (value: string) => {
  let result: any
  if (search && isFound.value) {
    const start = value.toLowerCase().indexOf(search.value.toLowerCase())
    let end
    if (start < 0) {
      return value
    } else {
      end = start + search.value.length
    }
    const part1 = value.slice(0, start)
    const part2 = value.slice(start, end)
    const part3 = value.slice(end)
    return h('span', [part1, h('mark', part2), part3])
  } else {
    result = value
  }
  return result
}

const handleClickBranch = () => {
  if (props.isRoot) {
    emit('click:root-icon')
    return
  }
  if (search?.value) return
  if (expandedPaths?.value?.has(hashedPath.value)) {
    for (const path of [...expandedPaths.value]) {
      if (!isHashedPathDescendant(hashedPath.value, path)) continue
      expandedPaths.value.delete(path)
    }
  } else {
    expandedPaths?.value?.add(hashedPath.value)
  }
}

const handleOpen = () => {
  if (props.isRoot || search?.value) return
  expandedPaths?.value?.add(hashedPath.value)
}

const handleClickSelfNode = () => {
  emit('click:node', props.path, props.node)
}

const handleOnSelfContextmenu = () => {
  emit('on:contextmenu', props.node, isLeafNode.value)
}

const handleOnContextmenu = (data: Tree, isLeaf: boolean) => {
  emit('on:contextmenu', data, isLeaf)
}

const handleClickSelfName = () => {
  emit('click:name', props.node, isLeafNode.value)
}

const handleClickName = (data: Tree, isLeaf: boolean) => {
  emit('click:name', data, isLeaf)
}

const handleClickNode = (path: string[], value: any) => {
  emit('click:node', path, value)
}

const handleGetScroll = (callback: (top: number) => void) => {
  emit('container:getscroll', callback)
}

const handleContainerScroll = (top: number) => {
  emit('container:scroll', top)
}

watch(isScrolledDown, (value, prev) => {
  if (prev || !value) return
  if (isLazyIndicatorVisible.value) {
    handleGetScroll(value => {
      scrollTopValue.value = value
    })
  } else {
    scrollTopValue.value = undefined
  }
  itemsPerPage.value += TREE_LAZY_COUNT
  nextTick(() => {
    if (scrollTopValue.value) {
      handleContainerScroll(scrollTopValue.value)
    }
  })
})
</script>

<style>
.ui-tree {
  &__item {
    @apply h-6;
    @apply flex shrink-0;
    @apply sticky top-0;
    @apply px-1 pr-[0.15rem];
    @apply bg-body hover:bg-body-gray;
    @apply rounded;

    &--active {
      @apply bg-indigo-50! dark:bg-gray-900!;
      @apply outline -outline-offset-1 outline-indigo-100 dark:outline-gray-800;
    }

    &--wide {
      @apply h-12;
      @apply px-4;
    }

    &--highlighted {
      @apply bg-indigo-100! dark:bg-indigo-900!;
    }
  }

  &__item-area {
    @apply flex flex-auto items-center;
    @apply px-1 gap-x-3;
    @apply overflow-hidden;
  }

  &__name {
    @apply shrink-0;
    @apply text-base sm:text-sm;
    @apply leading-5 sm:leading-tight;
    @apply text-gray-500 dark:text-gray-400;
    @apply overflow-hidden;

    > span {
      @apply block;
      @apply truncate;
    }

    &--wide {
      > span {
        @apply whitespace-normal;
        @apply line-clamp-2;
      }
    }
  }

  &__description {
    @apply text-sm sm:text-xs;
    @apply text-gray-400 dark:text-gray-500;
    @apply font-light;

    > span {
      @apply block;
      @apply truncate;
    }
  }

  &__value {
    @apply truncate;
  }

  &__actions {
    @apply flex items-center;
  }

  &__lazy {
    @apply h-px;
  }
}
</style>
